import { useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { useAuth } from '../../hooks/useAuth'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { useImagePickCrop } from '../../hooks/useImagePickCrop'
import { useProfileEditor } from '../../hooks/useProfileEditor'
import { classifyError } from '../../lib/errors'
import { UserAvatar } from '../../components/UserAvatar'
import { AvatarCropDialog } from '../../components/AvatarCropDialog'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ProfileHero } from '../../components/profile/ProfileHero'
import { ProfileStats } from '../../components/profile/ProfileStats'
import type { ProfileStat } from '../../components/profile/buildProfileStats'
import { AvatarPhotoButton } from './AvatarPhotoButton'
import { DisplayNameEditor } from './DisplayNameEditor'
import { SelfProfileActions } from './SelfProfileActions'

export function SelfProfileHero({ stats, statsLoading }: { stats: ProfileStat[]; statsLoading: boolean }) {
  const { user } = useAuth()
  const profiles = useUserProfiles(user ? [user.uid] : [])
  const { setAvatar, removeAvatar } = useProfileEditor(user?.uid ?? null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const { inputRef: fileInputRef, openPicker, onFileChange, cropSrc, closeCrop } = useImagePickCrop({
    maxBytes: 5 * 1024 * 1024,
    onInvalid: reason =>
      setUploadError(
        reason === 'not_image'
          ? fbt('Please select an image file.', 'Error message for non-image file upload')
          : fbt('Image must be under 5 MB.', 'Error message for oversized avatar upload'),
      ),
  })
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)

  const loadedProfile = user ? profiles[user.uid] : undefined
  const profile = loadedProfile ?? (user
    ? {
        uid: user.uid,
        displayName: user.displayName ?? '',
        photoURL: user.photoURL,
      }
    : undefined)
  const hasPhoto = Boolean(loadedProfile?.photoURL ?? user?.photoURL)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setUploadError(null)
    onFileChange(e)
  }

  async function handleCropConfirm(blob: Blob) {
    if (!user) return
    setUploading(true)
    setUploadError(null)
    try {
      await setAvatar(blob)
      closeCrop()
    } catch (err) {
      classifyError(err)
      toast.error(
        fbt('Upload failed. Please try again.', 'Toast shown when avatar upload fails').toString(),
      )
      throw err
    } finally {
      setUploading(false)
    }
  }

  async function handleRemoveAvatar() {
    if (!user) return
    setUploading(true)
    setUploadError(null)
    try {
      await removeAvatar()
    } catch (err) {
      classifyError(err)
      setUploadError(fbt('Failed to remove avatar. Please try again.', 'Error message when avatar removal fails'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <ProfileHero
        actions={<SelfProfileActions />}
        avatar={
          <div className="relative">
            <UserAvatar
              loading={!loadedProfile}
              profile={profile}
              size="2xl"
            />
            <AvatarPhotoButton
              hasPhoto={hasPhoto}
              uploading={uploading}
              onPick={() => openPicker()}
              onRemove={() => setRemoveConfirmOpen(true)}
            />
          </div>
        }
      >
        <DisplayNameEditor uid={user?.uid ?? null} loadedProfile={loadedProfile} />
        <ProfileStats stats={stats} loading={statsLoading} />
        {uploadError && (
          <p className="mt-2 text-sm text-destructive">{uploadError}</p>
        )}
      </ProfileHero>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <AvatarCropDialog
        open={cropSrc !== null}
        imageSrc={cropSrc}
        onCancel={closeCrop}
        onConfirm={handleCropConfirm}
      />
      <ConfirmDialog
        open={removeConfirmOpen}
        onOpenChange={setRemoveConfirmOpen}
        onConfirm={() => {
          setRemoveConfirmOpen(false)
          handleRemoveAvatar()
        }}
        title={fbt('Remove profile photo?', 'Confirm dialog title for removing the profile photo')}
        description={fbt(
          'Your profile photo will be removed and replaced with a default avatar.',
          'Confirm dialog body for removing the profile photo',
        )}
        confirmLabel={fbt('Remove', 'Confirm button to remove the profile photo')}
        destructive
      />
    </>
  )
}
